// costSheetRules — CHA-289. The SHARED, PURE term-validity rules (R-A / R-B / R-C) + their constants,
// MOVED VERBATIM from CostSheetTester.jsx (CHA-251), where they were born as the tester's analysis-only
// rules layer. The production verification pipeline (functions/productionVerification.js) now runs the
// SAME rules server-side over the same enriched rows, so the rule semantics/labels are defined once and
// can never drift between the tester UI and the pipeline (§8.2 — one source of truth).
//
// This is the canonical CLIENT (ESM) copy; the server twin is `functions/costSheetRules.js` (a CRA ESM
// bundle and a Cloud Functions CommonJS bundle can't share one module — same twin-file pattern as
// cpscBrokerCsv.js / csvSafe.js). The region between the byte-identical markers below MUST be kept
// byte-identical across the two files — guarded by functions/__tests__/costSheetRules.test.js.
//
// PURE — no I/O, no imports. `r` is one raw enriched row (plain-STRING columns) from
// production_open_wip_enriched_csv / production_2025_receipts_enriched_csv (getCostSheetRows shape).

/* ---- byte-identical region: keep in sync across src/shared/utils/costSheetRules.js + functions/costSheetRules.js (CHA-289) ---- */
// CHA-289 review round (Codex P2) — the SP27 validation cutoff, single-sourced here so the tester and the
// verification pipeline can never disagree on the season boundary. Rows dated before this (Open WIP by
// due_date, Received by trans_date) carry legacy pre-SP27 formulas and are EXCLUDED from term-rule
// validation, exactly like the tester's dateExclusion.
const SP27_CUTOFF_ISO = '2026-07-01';
// CHA-294 (operator ruling 2026-07-13, corrected same day) — DUTYP (DDP) is WI-termed for ALL divisions.
const s = (v) => String(v ?? '').trim();

const CANADA_WAREHOUSES = ['VBI', 'CLG', 'VNI']; // R-B deny-list. US warehouse = any non-blank shp2 not in here.
const FS_TERMS = ['FS', 'FS3'];
const NY_ONLY_TERMS = ['WI', 'FS']; // WI/FS are NY-office-only (R-C)
const KNOWN_TERMS = ['WI', 'FS', 'W3', 'FS3'];
const isCanadaWarehouse = (whse) => CANADA_WAREHOUSES.includes(whse);

// ② Term-validity rules. SEPARATE scopes — the ①-discount US-warehouse filter is NOT applied here.
// R-A (hard invalid): term∈{FS,FS3} on plan DUTYP or DIMPT.
// R-B (hard invalid): term∈{FS,FS3} as CPLAN to a Canada warehouse line (VBI/CLG/VNI).
// R-C (hard WARNING, per-row acknowledgeable): WI/FS are NY-office-only → a non-NY office using WI/FS, OR a
//   NY office not using FS/WI → "wrong term applied". Overridable (~1% legit exceptions expected).
// Returns an array of { rule, severity: 'invalid'|'warning', label }.
function termChecks(r) {
  const prd = s(r.pod_PrdTyp).toUpperCase();
  const term = s(r.terms).toUpperCase();
  const whse = s(r.shp2).toUpperCase();
  const office = s(r.poh_office).toUpperCase();
  const out = [];
  if ((prd === 'DUTYP' || prd === 'DIMPT') && FS_TERMS.includes(term)) {
    out.push({ rule: 'R-A', severity: 'invalid', label: `R-A invalid: term ${term} on ${prd} (first-sale not allowed on DUTYP/DIMPT)` });
  }
  if (prd === 'CPLAN' && FS_TERMS.includes(term) && isCanadaWarehouse(whse)) {
    out.push({ rule: 'R-B', severity: 'invalid', label: `R-B invalid: FS/FS3 CPLAN to Canada warehouse ${whse} (first-sale is US-only)` });
  }
  // CHA-294 (operator ruling 2026-07-13, corrected same day: ALL divisions): DUTYP (DDP) is WI-termed
  // BY DESIGN — judged by PRDTYP alone (not office-gated, not division-gated):
  //   WI → correct (never an office violation),
  //   W3 → wrong term for DUTYP, FS/FS3 → left to R-A (invalid; no R-C double-flag),
  //   off-list terms ('09', R6/R7/R15…) → unjudged pending their own rulings.
  const isDutypWiScope = prd === 'DUTYP';
  if (isDutypWiScope) {
    if (term === 'W3') {
      out.push({ rule: 'R-C', severity: 'warning', label: `R-C warning: DUTYP (DDP) must use WI, not W3 (operator ruling 2026-07-13) — verify (overridable)` });
    }
  }
  // DIMPT (direct import) is OFFICE-GATED (operator ruling 2026-07-17, correcting CHA-296's blanket-W3):
  //   NY office → WI (W3 flags as the wrong term), non-NY office → W3 (WI flags as the wrong term).
  //   FS/FS3 stay R-A's problem (kept out of the general R-C block below to avoid a double-flag);
  //   blank office / off-list terms unjudged. DIFOB0 NOT ruled (operator question pending).
  const isDimptScope = prd === 'DIMPT';
  if (isDimptScope && office) {
    if (office === 'NY' && term === 'W3') {
      out.push({ rule: 'R-C', severity: 'warning', label: `R-C warning: DIMPT NY office must use WI, not W3 (operator ruling 2026-07-17) — verify (overridable)` });
    } else if (office !== 'NY' && term === 'WI') {
      out.push({ rule: 'R-C', severity: 'warning', label: `R-C warning: DIMPT non-NY office (${office}) must use W3, not WI (operator ruling 2026-07-17) — verify (overridable)` });
    }
  }
  // R-C office logic only fires when we have a real office AND a recognized term (blank/off-list not
  // judged) — and never inside the DUTYP-WI or DIMPT ruling scopes above.
  if (!isDutypWiScope && !isDimptScope && office && KNOWN_TERMS.includes(term)) {
    if (office !== 'NY' && NY_ONLY_TERMS.includes(term)) {
      out.push({ rule: 'R-C', severity: 'warning', label: `R-C warning: non-NY office (${office}) using NY-only term ${term} — verify (overridable)` });
    } else if (office === 'NY' && !NY_ONLY_TERMS.includes(term)) {
      out.push({ rule: 'R-C', severity: 'warning', label: `R-C warning: NY office using ${term} (expected WI/FS) — verify (overridable)` });
    }
  }
  return out;
}

// ─── ① Discount check — CHA-380: MOVED VERBATIM from CostSheetTester.jsx (CHA-251 origin), the same
// extraction pattern as termChecks above, so the verification pipeline and the tester judge first-sale
// discounts with ONE implementation. Field mapping (§6.2.1 — verified live against both enriched _csv
// tables, CHA-251): FOB (full, pre-discount) = n300_cost · FOBHA (first-sale-adjusted, lower) = nFOBHA ·
// destination/warehouse = shp2. The discount uses FOB/FOBHA, NOT n600_cost — independent of the known
// n600_cost=0 CPLAN defect.

// Raw ERP export → number. Tolerant of $, commas, whitespace, blanks. (Twin of the tester's local num.)
function num(v) {
  if (v === null || v === undefined) return null;
  const str = String(v).replace(/[$,\s]/g, '').trim();
  if (str === '') return null;
  const n = Number(str);
  return Number.isFinite(n) ? n : null;
}
// 3-decimal money for check labels. (Twin of the tester's local fmt3.)
function fmt3(n) {
  return typeof n === 'number' && Number.isFinite(n)
    ? n.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
    : '—';
}
// CHA-312/314 — cents-rounding tolerance around the 15% first-sale placeholder (±0.2 discount points).
// A FOBHA rounded to the nearest cent off the exact 15% can land at ~14.97%; that's 'at spec 15%'. Used
// by the fsAtSpec15 shipped warning. (The fs15 'discount not honored' flag uses a plain <14% per CHA-314.)
const FS_SPEC_TOL = 0.002;
const DISCOUNT_STAGES = ['SHIPPED', 'RECEIVED', 'RECEIVD']; // ticket says {SHIPPED,RECEIVED}; data stores RECEIVD
const isUsWarehouse = (whse) => !!whse && !isCanadaWarehouse(whse); // present & not Canada → treated as US

// ① Discount check. Scope: plan=CPLAN · term∈{FS,FS3} · destination=US warehouse · stage∈{SHIPPED,RECEIVED}.
// Returns null when out of scope. Discount d = (FOB − FOBHA) / FOB (expected ~15%).
// Step-0 data-integrity guard runs FIRST: if FOB or FOBHA is null/zero/non-numeric the row is
// 'data-incomplete' (grey) — NEVER a red discount. A zeroed field must never masquerade as a bad discount
// (e.g. FOBHA=0 would compute d=100% ≈ strong-green; the guard catches it as grey instead).
// Bands: FOBHA>FOB (d<0) → error (Rule 1) · d<7% red · 7–12% yellow · 12–20% green · >20% strong-green+review.
function discountCheck(r) {
  const prd = s(r.pod_PrdTyp).toUpperCase();
  const term = s(r.terms).toUpperCase();
  const stage = s(r.stage).toUpperCase();
  const whse = s(r.shp2).toUpperCase();
  // Discount scope gate (all four must hold).
  if (prd !== 'CPLAN' || !FS_TERMS.includes(term)) return null;
  if (!isUsWarehouse(whse)) return null;
  if (!DISCOUNT_STAGES.includes(stage)) return null;

  const fob = num(r.n300_cost);
  const fobha = num(r.nFOBHA);
  // CHA-451 — the discount is measured against the FIRST-SALE value, and since CHA-438 that is fs_cost,
  // not nFOBHA. The ERP repurposed nFOBHA as the COST base, where on FS3 it carries only the 3% HA
  // deduction; measuring FOB against it reports "3% discount" on a line that actually earned ~20%.
  // pod_pkey 1349569: FOB 1.72, nFOBHA 1.66840 → 3.0%, but fs_cost 1.376 → 20.0%. Every flagged row on
  // the 2026-08-12 export carried a low-discount warning it did not deserve for exactly this reason.
  // With the flag off there is no arrived price, so nFOBHA remains the best available signal.
  const fsCost = num(r.fs_cost);
  const fsOn = s(r.fs_flag).toUpperCase() === 'Y' && fsCost !== null && fsCost > 0;
  const customs = fsOn ? fsCost : fobha;
  const customsName = fsOn ? 'FS cost (fs_cost)' : 'FOBHA (nFOBHA)';
  // Step 0: data-integrity guard — null / zero / NEGATIVE / missing on either input → can't compute → grey
  // (never red). FOB and the customs value are prices, always > 0; a ≤0 value is bad data, not a discount.
  // Guarding ≤0 (not just ==0) also closes the masquerade where a negative value would yield d>1 → a bogus
  // strong-green. Guards the value actually used, so a usable fs_cost is not rejected for a zeroed nFOBHA.
  if (fob === null || fob <= 0 || customs === null || customs <= 0) {
    const missing = [
      (fob === null || fob <= 0) && 'FOB (n300_cost)',
      (customs === null || customs <= 0) && customsName,
    ].filter(Boolean).join(', ');
    return { band: 'data-incomplete', d: null, fob, fobha, customs, fsOn, review: false,
      label: `data incomplete — can't compute discount (${missing} null/zero/negative)` };
  }
  const d = (fob - customs) / fob;
  if (d < 0) {
    return { band: 'error', d, fob, fobha, customs, fsOn, review: false,
      label: `invalid: ${fsOn ? 'FS cost' : 'FOBHA'} ($${fmt3(customs)}) > FOB ($${fmt3(fob)}) — Rule 1` };
  }
  // CHA-451 — the 0.20 edge needs a float guard. A first-sale price set at exactly 80% of FOB computes as
  // 0.20000000000000004 in IEEE754, which is > 0.20, so an exactly-20% discount fell into strong-green and
  // raised "review: verify real, not an error". 32 of the 53 flagged rows live sit exactly on that edge,
  // so without this the fix merely swaps one undeserved warning for another. BAND_EPS is a representation
  // epsilon, not a business tolerance — deliberately far tighter than FS_SPEC_TOL, so a genuine 20.05%
  // still reviews. Same class of defect as CHA-314, where a flag fired on a cents-rounding artifact.
  const BAND_EPS = 1e-9;
  let band; let review = false;
  if (d < 0.07) band = 'red';
  else if (d < 0.12) band = 'yellow';
  else if (d <= 0.20 + BAND_EPS) band = 'green';
  else { band = 'strong-green'; review = true; }
  // CHA-312 (operator 2026-07-15) — a SHIPPED FS/FS3 row still at the spec 15% (±0.2pt cents-rounding
  // tolerance) means the ACTUAL first-sale value never arrived: the 15% is the booking placeholder.
  // WARNING severity; ISSUED rows at 15% are expected (actuals come later) and never flag.
  // CHA-451 — "still at spec 15%" is a statement about the CUSTOMS value, so it reads whichever value the
  // discount was measured on. A flagged row sitting at 15% means the arrived price is itself the
  // placeholder; an unflagged one means the price never came back. Both are the same warning.
  const fsAtSpec15 = stage === 'SHIPPED' && Math.abs(d - 0.15) <= FS_SPEC_TOL;
  const pct = `${(d * 100).toFixed(1)}%`;
  const specNote = fsAtSpec15 ? ' — WARNING: shipped but still at spec 15% (actual first-sale value likely not received)' : '';
  return { band, d, fob, fobha, customs, fsOn, review, fsAtSpec15,
    label: (review ? `discount ${pct} (>20% — review: verify real, not an error)` : `discount ${pct}`) + specNote };
}

// CHA-297 — RULE_DESCRIPTORS: a PURE, render-ready description of every rule/scope this module
// implements (the Production → Verification 'Rules' panel + the read callable's `descriptors` field
// consume it). Text is DERIVED from the actual constants above (interpolated, never retyped), so a
// constant edit propagates to the panel automatically. Data only — no behavior lives here; termChecks
// stays the single judging implementation.
const RULE_DESCRIPTORS = [
  {
    id: 'R-A',
    name: 'First-sale terms on DUTYP/DIMPT',
    severity: 'invalid',
    scope: `pod_PrdTyp ∈ {DUTYP, DIMPT} with term ∈ {${FS_TERMS.join(', ')}}`,
    expectation: `First-sale terms (${FS_TERMS.join('/')}) are never valid on a DUTYP or DIMPT plan.`,
  },
  {
    id: 'R-B',
    name: 'First-sale CPLAN to a Canada warehouse',
    severity: 'invalid',
    scope: `pod_PrdTyp = CPLAN with term ∈ {${FS_TERMS.join(', ')}} shipping to a Canada warehouse (${CANADA_WAREHOUSES.join(', ')})`,
    expectation: 'First-sale is US-only — an FS/FS3 CPLAN line must not ship to a Canada warehouse.',
  },
  {
    id: 'R-C-OFFICE',
    name: 'NY-office term direction',
    severity: 'warning',
    scope: `office present and term ∈ {${KNOWN_TERMS.join(', ')}}, outside the DUTYP/DIMPT ruling scopes below`,
    expectation: `${NY_ONLY_TERMS.join('/')} are NY-office-only: a non-NY office must not use them, and the NY office must use one of them.`,
    notes: 'Overridable — ~1% legitimate exceptions expected. Blank offices and blank/off-list terms are not judged.',
  },
  {
    id: 'R-C-DUTYP-WI',
    name: 'DUTYP (DDP) is WI-termed',
    severity: 'warning',
    rulingDate: '2026-07-13',
    scope: 'pod_PrdTyp = DUTYP — ALL divisions, not office-gated (overrides the office logic in this scope)',
    expectation: 'DUTYP must use WI; W3 flags as the wrong term. FS/FS3 stay R-A (no double-flag); off-list terms are unjudged pending their own rulings.',
    notes: 'CHA-294 operator ruling 2026-07-13 (corrected same day: all divisions, was div 15/16).',
  },
  {
    id: 'R-C-DIMPT',
    name: 'DIMPT (direct import) term by office',
    severity: 'warning',
    rulingDate: '2026-07-17',
    scope: 'pod_PrdTyp = DIMPT — office-gated (overrides the general office logic in this scope)',
    expectation: 'DIMPT NY office must use WI; non-NY office must use W3 — the wrong one flags. FS/FS3 stay R-A (no double-flag); blank office / off-list terms are unjudged.',
    notes: 'Operator ruling 2026-07-17, correcting CHA-296 (which treated DIMPT as blanket-W3). DIFOB0 is NOT ruled (operator question pending).',
  },
  {
    id: 'SP27-CUTOFF',
    name: 'SP27 validation cutoff',
    severity: 'info',
    scope: `rows dated before ${SP27_CUTOFF_ISO} (Open WIP by due_date, Received by trans_date)`,
    expectation: 'Pre-SP27 rows carry legacy formulas and are EXCLUDED from term-rule validation; a missing or unparseable date cannot confirm the cutoff and is likewise skipped.',
  },
];

// CHA-437 — the HA (handling-agent) deduction, by payment term. The base a landed cost is built on is
// FOB x (1 - HA%): 100% on WI/FS, 97% on W3/FS3. It is NOT nFOBHA, which carries the first-sale CUSTOMS
// value and belongs to duty and tariff alone; using it for the total claims the first-sale benefit twice
// (CHA-435, ~$12M understated). Operator ruling 2026-08-10: "100% of the FOB in FS or 97% in FS3 - like
// we do in WI and W3".
//
// Lives HERE, in the byte-identical region, for the reason this file exists: the Cost Sheet Tester
// (CHA-436) and the verification pipeline (CHA-437) both judge the same rows against this number, and a
// second hand-maintained copy is exactly how the two surfaces would drift apart. Same 0/3 split as the
// commission rate (7% WI/FS, 9% W3/FS3) - the "3" in W3/FS3 IS the HA deduction. Verified against
// n309_HApct on every live CPLAN row in both enriched tabs, no exceptions.
const HA_PCT_BY_TERM = (terms) => (String(terms ?? '').trim().toUpperCase().match(/^(W3|FS3)$/) ? 3 : 0);
const costBaseFor = (fob, terms) => fob * (1 - HA_PCT_BY_TERM(terms) / 100);
/* ---- end byte-identical region ---- */

export { SP27_CUTOFF_ISO, CANADA_WAREHOUSES, FS_TERMS, NY_ONLY_TERMS, KNOWN_TERMS, RULE_DESCRIPTORS, isCanadaWarehouse, termChecks, discountCheck, FS_SPEC_TOL, HA_PCT_BY_TERM, costBaseFor };
